import { ZIndex } from "@/domains/design/constants";
import { ClassNamesArg, css, cx } from "@/domains/emotion";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import { memo } from "react";

export interface FloatingActionsProps extends React.PropsWithChildren {
  className?: ClassNamesArg;
  isOpen: boolean;
  verticalOffset?: number;
  bottom?: number | string;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const FloatingActions = memo<FloatingActionsProps>(function FloatingActions({
  verticalOffset = 0,
  bottom,
  isOpen,
  children,
  className,
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={cx(wrapperStyles, className)}
          initial={{ bottom: bottom ?? -65 }}
          animate={{ bottom: bottom ?? 10 + verticalOffset }}
          exit={{ bottom: bottom ?? -65 }}
          transition={{
            duration: 0.2,
            // cubic-bezier
            ease: [0.34, 1.56, 0.64, 1],
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
});

const wrapperStyles = css({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  justifyContent: "space-around",
  alignItems: "center",
  position: "fixed",
  zIndex: ZIndex.FloatingActions,
  // Center horizontally without blocking clicks on the sides
  width: "fit-content",
  left: 0,
  right: 0,
  marginInline: "auto",
  boxSizing: "border-box",
  maxWidth: "100%",
});

export const RightAlignedFloatingActions = styled(FloatingActions)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  width: "100%",
  justifyContent: "flex-end",
  paddingRight: theme.spacing.sm,
  marginBottom: `-${theme.spacing.sm}`,
}));

export const CenteredFloatingActions = styled(FloatingActions)({
  position: "absolute",
  bottom: 0,
  left: 16,
  right: 16,
  maxWidth: "calc(100% - 32px)",
  justifyContent: "center",
});

export const AnchoredFloatingActions = styled(FloatingActions)<{
  $top: number;
  $right?: number;
  $center?: number;
}>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  ${({ $right }) => typeof $right === "number" && `right: ${$right}px;`}
  ${({ $center }) =>
    typeof $center === "number" &&
    `
    left: ${$center}px;
    transform: translateX(-50%);
    right: auto;
  `}
`;
